//src/app/features/conversations/Components/Message/index.tsx
import { IMessage } from 'app/services/messages';
import { ISMSMessage } from 'app/services/sms/sms';
import { IUser } from 'app/services/user';
import UserBox from 'Components/UserBox';
import { createContext, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'utils/AuthProvider';
import MessageAttachements from './MessageAttachements';
import MessageContent from './MessageContent';
import MessageDate from './MessageDate';

const currentUserTheme = {
  bgLight: 'bg-green bg-opacity-10',
  bgMediumLight: 'bg-green bg-opacity-20',
  initialsColor: 'text-green',
};

const otherUserTheme = {
  bgLight: 'bg-gray-light bg-opacity-10',
  bgMediumLight: 'bg-gray-dark bg-opacity-20',
  initialsColor: 'text-gray-dark',
};

export const MessageContext = createContext<{
  theme: {
    bgLight: string;
    bgMediumLight: string;
    initialsColor: string;
  };
  byCurrentUser: boolean;
  userInfo?: IUser;
}>({
  theme: {
    bgLight: '',
    bgMediumLight: '',
    initialsColor: '',
  },
  byCurrentUser: false,
});

interface MessageProps {
  message: IMessage | ISMSMessage;
}

const Message = forwardRef(({ message }: MessageProps, ref: any) => {
  const { userId, isClient, role } = useAuth();
  const navigate = useNavigate();
  const byCurrentUser = message.sender.id === userId;
  const isSystemMessage = new RegExp(/^(<aoa>.*<aoa>)$/g).test(
    (message as ISMSMessage).body || message.content,
  );

  const userInfo = message.sender;
  let theme = byCurrentUser ? currentUserTheme : otherUserTheme;

  return (
    <>
      {isSystemMessage ? (
        <div className="my-1.5 flex justify-center mx-2 italic text-xs">
          <div className="flex p-2 rounded-xl shadow text-gray-dark font-light bg-gray-light bg-opacity-10 justify-center">
            {(message as ISMSMessage).body || message.content}
          </div>
        </div>
      ) : (
        <MessageContext.Provider value={{ theme, byCurrentUser, userInfo }}>
          <UserBox
            ref={ref}
            className={`px-4 md-max:px-2 py-6 md-max:py-2 text-sm md-max:text-xs w-5/6 md-max:w-11/12 ${
              byCurrentUser
                ? 'self-end md-max:mr-2 mr-4'
                : 'self-start ml-4 md-max:ml-2'
            }`}
            theme={byCurrentUser ? 'green' : 'gray'}
            user={userInfo!}
            right={
              <div className="md-max:hidden self-start flex items-center justify-start">
                <MessageDate
                  value={message.created_at}
                  className="text-sm text-gray-700"
                />
              </div>
            }
            content={
              <>
                <div className="hidden md-max:flex justify-end">
                  <MessageDate
                    value={message.created_at}
                    className="text-xs text-gray-700"
                  />
                </div>
                <div className="message-preview mt-4">
                  <MessageContent
                    content={(message as ISMSMessage).body || message.content}
                    className="mx-1 break-all text-gray-dark text-opacity-70"
                  />
                  <MessageAttachements documents={message.documents} />
                </div>
              </>
            }
          />
        </MessageContext.Provider>
      )}
    </>
  );
});

export default Message;
